import { useCallback, useEffect, useRef, useState } from "react";
import { Bookmark, Loader2 } from "lucide-react";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import PostCard from "@/components/PostCard";
import SubSettingsShell from "@/components/SubSettingsShell";

/**
 * Saved — iOS `settings/saved.tsx`.
 *
 * Infinite-scroll list of posts the current user has bookmarked.  Uses
 * the shared iOS-style sub-settings header (back arrow + centred title)
 * for consistency with the rest of the Settings tree.
 */

const BRAND = "#7638FA";

export default function Saved() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const sentinelRef = useRef(null);
  const seenIds = useRef(new Set());
  const requestSeq = useRef(0);

  const load = useCallback(async (p) => {
    const myReq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await api.get(EP.mySaved(p, 10));
      if (myReq !== requestSeq.current) return;
      const d = unwrap(res.data);
      const list = Array.isArray(d)
        ? d
        : d?.items || d?.posts || d?.results || d?.data || [];
      if (p === 1) {
        seenIds.current = new Set(
          list.map((it) => it.id ?? it.post_id).filter((x) => x != null)
        );
        setItems(list);
      } else {
        const fresh = list.filter((it) => {
          const id = it.id ?? it.post_id;
          if (id == null) return true;
          if (seenIds.current.has(id)) return false;
          seenIds.current.add(id);
          return true;
        });
        setItems((prev) => [...prev, ...fresh]);
      }
      setHasMore(list.length >= 10);
      setErr("");
    } catch (e) {
      if (myReq !== requestSeq.current) return;
      setErr(apiError(e));
      setHasMore(false);
    } finally {
      if (myReq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const next = page + 1;
          setPage(next);
          load(next);
        }
      },
      { rootMargin: "400px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [page, hasMore, loading, load]);

  const removePost = (id) =>
    setItems((prev) => prev.filter((p) => (p.id ?? p.post_id) !== id));

  return (
    <SubSettingsShell
      title="Saved"
      subtitle="Posts you've saved. Only visible to you."
      testId="saved-screen"
    >
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: BRAND }} />
        </div>
      ) : err ? (
        <div
          className="rounded-2xl p-4 flex items-center justify-between gap-3"
          style={{ backgroundColor: "rgba(255, 59, 48, 0.13)" }}
          data-testid="saved-error"
        >
          <p className="text-[#FF3B30] text-sm flex-1">{err}</p>
          <button
            type="button"
            onClick={() => load(1)}
            className="text-[#FF3B30] text-sm font-bold underline underline-offset-4"
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-[20px] border border-border bg-card py-12 flex flex-col items-center text-center px-6"
          data-testid="saved-empty"
        >
          <Bookmark size={22} className="text-muted-foreground mb-2.5" strokeWidth={1.7} />
          <p className="text-foreground text-[15px] font-bold">
            You haven&apos;t saved anything yet.
          </p>
          <p className="text-muted-foreground text-[12.5px] mt-1">
            Tap the bookmark icon on any post to save it here.
          </p>
        </div>
      ) : (
        <div className="rounded-[20px] border border-border bg-card overflow-hidden">
          <div className="divide-y divide-border">
            {items.map((p) => (
              <PostCard key={p.id ?? p.post_id} post={p} onDeleted={removePost} />
            ))}
          </div>
          {hasMore && (
            <div
              ref={sentinelRef}
              className="py-8 flex justify-center text-muted-foreground text-sm border-t border-border"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: BRAND }} />
              ) : (
                <span>Loading more…</span>
              )}
            </div>
          )}
        </div>
      )}
    </SubSettingsShell>
  );
}
